// Persistent two-column layout. Sidebar shows only when there is a
// session (user is unlocked). Lifecycle screens (picker, unlock,
// wizard) render full-width with no sidebar.
//
// On mobile (<md) the sidebar lives inside a NavDrawer instead of
// the inline flow, so the main column gets full width. The drawer
// open state is held in the UI store so Topbar (sibling) can open it
// via the hamburger.

import type { ReactNode } from "react";
import { useSession } from "../../stores/session";
import { useUi } from "../../stores/ui";
import { useProgressSubscription } from "../../hooks/useProgressSubscription";
import { useCacheFlushLifecycle } from "../../hooks/useCacheFlushLifecycle";
import { useInactivityLock } from "../../hooks/useInactivityLock";
import { useIsMobile } from "../../hooks/useIsMobile";
import { NavDrawer } from "../ui/nav-drawer";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { PendingRotationBanner } from "./PendingRotationBanner";

export function AppShell({ children }: { children: ReactNode }) {
  const session = useSession((s) => s.session);
  const autoLockMinutes = useUi((s) => s.autoLockMinutes);
  const drawerOpen = useUi((s) => s.drawerOpen);
  const setDrawerOpen = useUi((s) => s.setDrawerOpen);
  const isMobile = useIsMobile();
  useInactivityLock(
    autoLockMinutes > 0 ? autoLockMinutes * 60_000 : 24 * 60 * 60_000,
  );
  useProgressSubscription();
  useCacheFlushLifecycle();

  const showInlineSidebar = Boolean(session) && !isMobile;
  const showDrawer = Boolean(session) && isMobile;
  // The pending-rotation banner is only meaningful on authenticated
  // views (post-master-unlock). The lifecycle screens (`/setup`,
  // `/master`, `/vaults/add`) have no session and the banner self-
  // hides via its `appConfig` null-guard, but gating here keeps the
  // shell clean even if session bookkeeping changes later.
  const showBanner = Boolean(session);

  return (
    <div className="flex h-full min-h-screen bg-bg text-text">
      {showInlineSidebar && <Sidebar />}
      <div className="flex flex-1 flex-col">
        {session && <Topbar />}
        {showBanner && <PendingRotationBanner />}
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
      {showDrawer && (
        <NavDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          title="Navigation"
        >
          <Sidebar />
        </NavDrawer>
      )}
    </div>
  );
}
